import { ArcElement, CategoryScale, Legend, Tooltip } from "chart.js";
import Chart from "chart.js/auto";
import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  ArrowIcon,
  FieldViewIcon,
  LocationsIcon,
  ProceduresIcon,
  ReportsIcon,
  SettingsIcon,
  WorkforceIcon
} from "../../Assets/assets";
import Settings from "../../Components/Settings/Settings";
import SideBar from "../../Components/SideBar/SideBar";
import { logout, setTheme } from "../../Redux/authentication/actions";
import {
  updatePasswordAction,
  updateProfileAction
} from "../../Redux/profile/action";
import { rippleEffect } from "../../Utils/Constants/Constants";
import Calls from "../Calls/Calls";
import FieldView from "../FieldView/FieldView";
import Locations from "../Locations/Locations";
import Procedures from "../Procedures/Procedures";
import Reports from "../Reports/Reports";
import Support from "../Support/Support";
import Workforce from "../Workforce/Workforce";
import { updateNotification } from "../../Redux/notifications/actions";
import NotificationPanel from "../../Components/NotificationPanel/NotificationPanel";
import { useResizeObserver } from "../../CustomHooks/useResizeObserver";

Chart.register(CategoryScale, ArcElement, Tooltip, Legend);

const buildMenuItem = (condition, item) => (condition ? [item] : []);

function Home(props) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const force = searchParams.get("force");
  const homeContainerRef = useRef(null);
  const { isMobileView } = useResizeObserver(homeContainerRef);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [selectedSubMenu, setSelectedSubMenu] = useState(null);

  const menu = useMemo(
    () => [
      ...buildMenuItem(isMobileView, {
        title: "Profile settings",
        icon: <SettingsIcon color="var(--tertiaryColor6)" />,
        path: "/settings"
      }),
      ...buildMenuItem(!isMobileView, {
        title: "Field view",
        icon: <FieldViewIcon color="var(--tertiaryColor6)" />,
        path: "/"
      }),
      {
        title: "Locations",
        icon: <LocationsIcon color="var(--tertiaryColor6)" />,
        path: "/locations/overview",
        subMenu: [
          { title: "Overview", path: "/locations/overview" },
          ...buildMenuItem(props.claims?.complaints?.read?.all, {
            title: "Complaints",
            path: "/locations/complaints"
          }),
          ...buildMenuItem(props.claims?.procedureHistory?.read, {
            title: "Procedure history",
            path: "/locations/procedureHistory"
          }),
          ...buildMenuItem(props.claims?.surveillance?.read, {
            title: "Live feeds",
            path: "/locations/liveFeed"
          })
        ]
      },
      ...buildMenuItem(props.claims?.employees?.read, {
        title: "Workforce",
        icon: <WorkforceIcon color="var(--tertiaryColor6)" />,
        path: "/workforce"
      }),
      ...buildMenuItem(props.claims?.procedures?.read, {
        title: "Procedures",
        icon: <ProceduresIcon color="var(--tertiaryColor6)" />,
        path: "/procedures"
      }),
      ...buildMenuItem(props.claims?.files?.read, {
        title: "Reports",
        icon: <ReportsIcon color="var(--tertiaryColor6)" />,
        path: "/reports"
      })
    ],
    // eslint-disable-next-line
    [
      isMobileView,
      props.claims?.complaints?.read?.all,
      props.claims?.procedureHistory?.read,
      props.claims?.surveillance?.read,
      props.claims?.employees?.read,
      props.claims?.procedures?.read,
      props.claims?.files?.read
    ]
  );

  useEffect(() => {
    if (!menu.length) return;

    const pathParts = location.pathname?.split("/").filter(Boolean);

    if (isMobileView && pathParts.length === 0) return;

    const selected =
      pathParts.length === 0
        ? menu[0]
        : menu.find((data) =>
            pathParts.includes(data?.path?.split("/")[1] || "")
          );

    setSelectedMenu(selected || null);

    if (selected?.subMenu) {
      const sub = selected.subMenu.find((data) =>
        pathParts[1]?.includes(data?.path?.split("/")[2] || "")
      );
      setSelectedSubMenu(sub || null);
    } else {
      setSelectedSubMenu(null);
    }
    // eslint-disable-next-line
  }, [window.location.pathname, menu]);

  return (
    <div className="inherit-parent-width inherit-parent-height">
      <div className="display-flex inherit-parent-width inherit-parent-height">
        <div
          style={{ minWidth: "300px", maxWidth: "400px", width: "15%" }}
          className="inherit-parent-height"
        >
          {menu.map((data, i) => (
            <MenuCard
              key={i}
              data={data}
              setShow={
                data.subMenu &&
                ["overview", "complaints", "procedureHistory", "liveFeed"].includes(
                  window.location.pathname.split("/")[2]
                )
              }
              selectedMenu={selectedMenu}
              selectedSubMenu={selectedSubMenu}
              navigate={props.navigate}
            />
          ))}
        </div>
        <div
          style={{
            minWidth: "calc(100% - 300px)",
            maxWidth: "calc(100% - 400px)",
            width: "85%"
          }}
          className="inherit-parent-height"
        >
          <Routes>
            <Route
              index
              element={
                <div className="padding-larger inherit-parent-height overflow-hidden">
                  <FieldView isAuth={props.isAuth} claims={props.claims} />
                </div>
              }
            />
            <Route
              path="/locations/*"
              element={
                <div className="padding-larger inherit-parent-height overflow-hidden">
                  <Locations {...props} />
                </div>
              }
            />
            {props.claims?.employees?.read ? (
              <Route
                path="/workforce/*"
                element={
                  <div className="padding-larger inherit-parent-height overflow-hidden">
                    <Workforce {...props} />
                  </div>
                }
              />
            ) : (
              <></>
            )}
            <Route path="/devices/*" element={<div>/devices</div>} />
            <Route
              path="/calls/*"
              element={
                <div className="padding-larger inherit-parent-height overflow-hidden">
                  <Calls navigate={props.navigate} />
                </div>
              }
            />
            {props.claims?.procedures?.read ? (
              <Route
                path="/procedures/*"
                element={
                  <div className="padding-larger inherit-parent-height overflow-hidden">
                    <Procedures
                      navigate={props.navigate}
                      claims={props.claims}
                    />
                  </div>
                }
              />
            ) : (
              <></>
            )}

            {props.claims?.files?.read ? (
              <Route
                path="/reports/*"
                element={
                  <div className="padding-larger inherit-parent-height overflow-hidden">
                    <Reports navigate={props.navigate} claims={props.claims} />
                  </div>
                }
              />
            ) : (
              <></>
            )}

            <Route
              path="/support/*"
              element={
                <div className="inherit-parent-height overflow-hidden padding-larger">
                  <Support isMobileView={isMobileView} />
                </div>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {force === "notification" && (
            <NotificationPanel
              notifications={props.notifications}
              updateNotification={props.updateNotification}
              navigate={props.navigate}
              notification={props.notification}
            />
          )}
          {force === "setting" && (
            <SideBar navigate={props.navigate} title="Settings">
              <Settings
                logout={props.logout}
                auth={props.auth}
                profile={props.profile}
                onThemeClick={props.setTheme}
                updateProfile={props.updateProfile}
                updatePassword={props.updatePassword}
              />
            </SideBar>
          )}
        </div>
      </div>
    </div>
  );
}
const mapStateToProps = function(state) {
  return {
    profile: state.profile,
    auth: state.auth,
    notifications: state.notifications
  };
};

const mapDispatchToProps = function() {
  return {
    logout: () => logout(),
    updateProfile: (file, data) => updateProfileAction(file, data),
    updatePassword: (data) => updatePasswordAction(data),
    setTheme: (data) => setTheme(data),
    updateNotification: (type, ruleId, docId) =>
      updateNotification(type, ruleId, docId)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);

const MenuCard = (props) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(props.setShow);
  }, [props.setShow, show]);

  return (
    <>
      <div
        data-cy={`${props.data.title?.toUpperCase()}-MENU-CARD`}
        onClick={(event) => {
          rippleEffect(event);
          if (props.data.subMenu) {
            setShow(!show);
            props.navigate(props.data.path);
          } else {
            props.navigate(props.data.path);
          }
        }}
        className={`display-flex margin-top-large margin-left-default margin-right-default padding-default flex-align-items-center flex-justify-content-space-between cursor-pointer ripple-element ${
          props.selectedMenu?.title === props.data?.title
            ? "background-color-tertiaryColor1"
            : ""
        }`}
      >
        <div className="display-flex flex-justify-content-center flex-align-items-center">
          <div
            style={{
              height: "20px",
              width: "20px"
            }}
          >
            {props.data.icon}
          </div>
          <div
            style={{
              fontWeight: "500"
            }}
            className="padding-left-large font-color-tertiaryColor"
          >
            {props.data.title}
          </div>
        </div>
        {props.data.subMenu && (
          <div
            style={{
              height: "16px",
              width: "16px"
            }}
          >
            <ArrowIcon color="var(--tertiaryColor6)" />
          </div>
        )}
      </div>
      {show && (
        <div className="padding-left-larger padding-top-large">
          {props.data?.subMenu?.map((ele, i) => (
            <div
              data-cy={`${ele.title?.toUpperCase()}-SUB-MENU-CARD`}
              key={i}
              onClick={() => {
                props.navigate(ele.path);
              }}
              className={`display-flex margin-right-large `}
            >
              <div
                style={{
                  width: "2px",
                  height: "36px",
                  ...(props.selectedSubMenu?.title === ele?.title
                    ? { backgroundColor: "var(--tertiaryColor6)" }
                    : {})
                }}
                className={`margin-right-large margin-left-default ${
                  props.selectedSubMenu?.title === ele?.title
                    ? ""
                    : "background-color-tertiaryColor1"
                }`}
              />
              <div
                style={{
                  fontWeight: "400"
                }}
                onClick={(event) => {
                  rippleEffect(event);
                }}
                className={`font-color-tertiaryColor padding-default ripple-element inherit-parent-width ${
                  props.selectedSubMenu?.title === ele?.title
                    ? "background-color-tertiaryColor1"
                    : ""
                }`}
              >
                {ele.title}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

};
